import type { FieldToken } from "models";

/**
 * Loose ProseMirror/TipTap JSON node. We intentionally keep this permissive —
 * the normalizer's whole job is to take untrusted/foreign JSON (e.g. produced by
 * converting claude.ai/design HTML) and reduce it to our supported vocabulary.
 */
export interface JSONNode {
  type?: string;
  text?: string;
  attrs?: Record<string, any>;
  marks?: Array<{ type?: string; attrs?: Record<string, any> }>;
  content?: JSONNode[];
  [key: string]: any;
}

export type NodeGroup = "block" | "inline";

export interface NodeRule {
  group: NodeGroup;
  /**
   * Allowed attribute names mapped to their default value.
   * `undefined` default = attribute is allowed but only kept when present
   * (no default injected).
   */
  attrs: Record<string, unknown>;
  /** Context for child nodes. Omit for atoms / leaves with no children. */
  childCtx?: NodeGroup;
}

/**
 * The supported node vocabulary — must stay in lock-step with the editor's
 * ExtensionKit (src/pages/tip-tap-editor/extensions/extension-kit.ts). Any node
 * type not listed here is unwrapped (children promoted) or dropped.
 */
export const ALLOWED_NODES: Record<string, NodeRule> = {
  // Block
  paragraph: { group: "block", attrs: { textAlign: "left" }, childCtx: "inline" },
  heading: { group: "block", attrs: { textAlign: "left", level: 1 }, childCtx: "inline" },
  columns: { group: "block", attrs: { layout: "two-column" }, childCtx: "block" },
  column: { group: "block", attrs: { position: "" }, childCtx: "block" },
  bulletList: { group: "block", attrs: {}, childCtx: "block" },
  orderedList: { group: "block", attrs: { start: 1 }, childCtx: "block" },
  listItem: { group: "block", attrs: {}, childCtx: "block" },
  signature: { group: "block", attrs: {}, childCtx: "inline" },
  imageBlock: {
    group: "block",
    attrs: { src: "", width: "100%", align: "center", type: "image", alt: undefined, imageId: undefined },
  },
  horizontalLine: { group: "block", attrs: { color: "#000000", height: 5, width: 100, alignment: "center" } },
  spacerBlock: { group: "block", attrs: { height: 50 } },
  pageBreak: { group: "block", attrs: {} },
  table: { group: "block", attrs: { align: undefined, containerWidth: undefined }, childCtx: "block" },
  tableRow: { group: "block", attrs: {}, childCtx: "block" },
  tableCell: {
    group: "block",
    attrs: { colspan: 1, rowspan: 1, colwidth: undefined, backgroundColor: undefined },
    childCtx: "block",
  },
  tableHeader: {
    group: "block",
    attrs: { colspan: 1, rowspan: 1, colwidth: undefined, backgroundColor: undefined },
    childCtx: "block",
  },

  // Inline
  text: { group: "inline", attrs: {} },
  mention: { group: "inline", attrs: { id: undefined, label: undefined } },
  hardBreak: { group: "inline", attrs: {} },
};

/**
 * Allowed marks mapped to their allowed attribute names (empty = no attrs).
 * Marks outside this set are stripped from text nodes.
 */
export const ALLOWED_MARKS: Record<string, string[]> = {
  bold: [],
  italic: [],
  strike: [],
  code: [],
  underline: [],
  link: ["href", "target", "rel", "class"],
  textStyle: ["color", "fontSize"],
};

/** Valid column layouts (MultiColumn/Columns.ts ColumnLayout). */
export const VALID_COLUMN_LAYOUTS = ["sidebar-left", "sidebar-right", "two-column"] as const;

/**
 * The 9 valid field-token ids. Built as an exhaustive Record<FieldToken, …> so
 * that adding a token to the union (models/field-tokens.ts) without updating
 * this list is a compile error.
 */
const FIELD_TOKEN_PRESENCE: Record<FieldToken, true> = {
  company: true,
  projectName: true,
  projectRef: true,
  createDate: true,
  client: true,
  projectAddress: true,
  userName: true,
  userPosition: true,
  reportNotes: true,
};

export const VALID_FIELD_TOKEN_IDS = new Set<string>(Object.keys(FIELD_TOKEN_PRESENCE));
